import { DEFAULT_CONTEXT, DEFAULT_GUARDS, DONE_TRANSITION_ACTION_NAME, ERROR_TRANSITION_ACTION_NAME, EVENTLESS_TRANSITION_ACTION_NAME, INITIAL_TRANSITION_ACTION_NAME } from './constants';
import { getActionByName, getEventName, getInitialState, getServiceByName, getTransitionActions, getTransitionCondition, getTransitionTarget, isDevelopment, isEventlessTransition, isServiceNode, makeArray } from './util';
export class StateMachine {
  constructor(machine, inspector) {
    this.status = 'initializing';
    this.machineDefinition = machine;
    this.initialState = getInitialState(machine);
    this.context = machine.context || DEFAULT_CONTEXT;
    this.guards = machine.guards || DEFAULT_GUARDS;
    this.inspector = inspector;
  }
  start() {
    if (!this.isInitializing()) {
      return;
    }
    this.startRunning();
  }
  implicitTransition(currentState) {
    const state = this.machineDefinition.states[currentState || this.initialState];
    if (isEventlessTransition(state)) {
      return this.transitionForEventless(currentState || this.initialState);
    }
    if (!isServiceNode(state)) {
      return;
    }
    return this.transitionForService(this.initialState, state, {
      type: INITIAL_TRANSITION_ACTION_NAME
    }, []);
  }
  stop() {
    this.stopRunning();
  }
  setContext(context) {
    this.context = context;
  }
  noop(state, event) {
    this.inspector.add({
      type: 'fsm.transition.noop',
      description: 'No transition change',
      event
    });
    return {
      state,
      event,
      actions: [],
      context: this.context
    };
  }
  transition(currentState, event) {
    const state = this.machineDefinition.states[currentState];
    const eventName = getEventName(event);
    if (!this.isRunning()) {
      throw new Error(`Machine is not running: Could not respond to ${eventName}, machine is "${this.status}"`);
    }
    if (!state && isDevelopment()) {
      throw new Error(`State "${currentState}" not found when responding to "${eventName}"`);
    }
    const topLevelTransitions = this.machineDefinition.on || {};
    const transitionNode = (state === null || state === void 0 ? void 0 : state.on) || {};
    const transitions = transitionNode[eventName] || topLevelTransitions[eventName];
    if (!transitions) {
      if (isDevelopment()) {
        throw new Error(`No transition for event: ${eventName} in state: ${currentState}`);
      } else {
        return this.noop(currentState, event);
      }
    }
    const allowedTransitions = makeArray(transitions).filter(t => {
      return this.transitionGuardCheck(t, this.context, event);
    });
    if (allowedTransitions.length === 0) {
      this.inspector.add({
        type: 'fsm.transition.skip',
        description: 'Current state has no allowed transitions',
        event
      });
      return this.noop(currentState, event);
    }
    const transition = allowedTransitions[0];
    const nextStateName = getTransitionTarget(transition);
    const nextState = this.machineDefinition.states[nextStateName || currentState];
    if (!nextState) {
      if (isDevelopment()) {
        throw new Error(`State "${nextStateName}" not found`);
      }
      return this.noop(currentState, event);
    }
    const actions = this.getExecutableActionsForTransition(transition);
    this.inspector.add({
      type: 'fsm.transition.take',
      description: 'Transitioning to a new state',
      data: {
        nextState: nextStateName || currentState,
        previousState: currentState,
        isService: isServiceNode(nextState)
      },
      event
    });
    if (isServiceNode(nextState)) {
      return this.transitionForService(currentState, nextState, event, actions, nextStateName);
    }
    return {
      state: nextStateName || currentState,
      context: this.context,
      actions,
      event
    };
  }
  transitionForEventless(currentState) {
    const state = this.machineDefinition.states[currentState];
    const event = {
      type: EVENTLESS_TRANSITION_ACTION_NAME
    };
    if (!isEventlessTransition(state)) {
      return this.noop(currentState, event);
    }
    const transitions = state.always;
    const allowedTransitions = makeArray(transitions).filter(t => {
      return this.transitionGuardCheck(t, this.context, event);
    });
    if (allowedTransitions.length === 0) {
      this.inspector.add({
        type: 'fsm.transition.skip',
        description: 'Eventless transition has no allowed transitions',
        event: {
          type: EVENTLESS_TRANSITION_ACTION_NAME
        }
      });
      return this.noop(currentState, event);
    }
    const transition = allowedTransitions[0];
    const nextStateName = getTransitionTarget(transition);
    const actions = this.getExecutableActionsForTransition(transition);
    this.inspector.add({
      type: 'fsm.transition.take',
      description: 'Eventless transition',
      data: {
        nextState: nextStateName,
        previousState: currentState
      },
      event: {
        type: EVENTLESS_TRANSITION_ACTION_NAME
      }
    });
    return {
      state: nextStateName || currentState,
      context: this.context,
      actions,
      event
    };
  }
  transitionForService(currentState, nextState, event, transitionActions, nextStateName) {
    const stateNode = nextState;
    const service = getServiceByName(this.machineDefinition, stateNode.invoke.src);
    const execService = async () => {
      try {
        const result = await service(this.context, event);
        const newEvent = {
          type: DONE_TRANSITION_ACTION_NAME,
          data: result
        };
        if (stateNode.invoke.onDone) {
          const allowedTransitions = makeArray(stateNode.invoke.onDone).filter(t => {
            return this.transitionGuardCheck(t, this.context, newEvent);
          });
          if (allowedTransitions.length === 0) {
            this.inspector.add({
              type: 'fsm.transition.skip',
              description: 'Service transition has no allowed transitions',
              event: newEvent
            });
            return this.noop(currentState, newEvent);
          }
          const transition = allowedTransitions[0];
          const latestState = getTransitionTarget(transition);
          const actions = this.getExecutableActionsForTransition(transition);
          this.inspector.add({
            type: 'fsm.transition.take',
            description: 'Service transition',
            data: {
              nextState: latestState || currentState,
              previousState: currentState
            },
            event: {
              type: DONE_TRANSITION_ACTION_NAME
            }
          });
          return {
            state: latestState || currentState,
            actions,
            event: newEvent,
            context: this.context
          };
        } else {
          this.inspector.add({
            type: 'fsm.transition.skip',
            description: 'Service transition has no defined transitions',
            event: newEvent
          });
          return this.noop(currentState, newEvent);
        }
      } catch (error) {
        const newEvent = {
          type: ERROR_TRANSITION_ACTION_NAME,
          data: error
        };
        if (stateNode.invoke.onError) {
          const allowedTransitions = makeArray(stateNode.invoke.onError).filter(t => {
            return this.transitionGuardCheck(t, this.context, newEvent);
          });
          if (allowedTransitions.length === 0) {
            this.inspector.add({
              type: 'fsm.transition.skip',
              description: 'Service error transition has no allowed transitions',
              event: newEvent
            });
            return this.noop(currentState, newEvent);
          }
          const transition = allowedTransitions[0];
          const latestState = getTransitionTarget(transition);
          const actions = this.getExecutableActionsForTransition(transition);
          this.inspector.add({
            type: 'fsm.transition.take',
            description: 'Service error transition',
            data: {
              nextState: latestState || currentState,
              previousState: currentState
            },
            event: {
              type: ERROR_TRANSITION_ACTION_NAME
            }
          });
          return {
            state: latestState || currentState,
            actions,
            event: newEvent,
            context: this.context
          };
        } else {
          this.inspector.add({
            type: 'fsm.transition.skip',
            description: 'Service error transition has no defined transitions',
            event: newEvent
          });
          return this.noop(currentState, newEvent);
        }
      }
    };
    return {
      state: nextStateName || currentState,
      exec: execService,
      event,
      context: this.context,
      actions: transitionActions
    };
  }
  getExecutableActionsForTransition(transition) {
    const actions = getTransitionActions(transition);
    return actions.map(action => {
      return {
        type: action,
        exec: (context, event) => {
          const fn = getActionByName(this.machineDefinition, action);
          try {
            this.inspector.add({
              type: 'fsm.action.resolve',
              description: `Executing action ${action}`,
              data: {
                action
              }
            });
            return fn(context, event);
          } catch (error) {
            this.inspector.add({
              type: 'fsm.action.error',
              error,
              data: {
                action
              }
            });
            console.error(`Error executing action: ${action}`);
            console.error(error);
          }
        }
      };
    });
  }
  hasTag(state, tag) {
    const stateNode = this.machineDefinition.states[state];
    if (!stateNode) {
      this.inspector.add({
        type: 'fsm.state.missing',
        description: 'Could not find state when checking for tag',
        data: {
          tag,
          state
        }
      });
      console.error(`Could not find tag for missing state: ${state}`);
      return false;
    }
    const tags = stateNode.tags || [];
    return tags.includes(tag);
  }
  isInitializing() {
    return this.status === 'initializing';
  }
  isRunning() {
    return this.status === 'running';
  }
  isStopped() {
    return this.status === 'stopped';
  }
  startRunning() {
    this.status = 'running';
  }
  stopRunning() {
    this.status = 'stopped';
  }
  transitionGuardCheck(transition, context, event) {
    const guardName = getTransitionCondition(transition);
    if (!guardName) {
      return true;
    }
    const guardFn = this.guards[guardName];
    if (!guardName || !guardFn) {
      this.inspector.add({
        type: 'fsm.guard.error',
        description: `No guard found for ${guardName}`,
        event
      });
      return true;
    }
    const result = guardFn(context, event);
    if (result) {
      this.inspector.add({
        type: 'fsm.guard.take',
        description: `Guard ${guardName} passed`,
        event
      });
    } else {
      this.inspector.add({
        type: 'fsm.guard.skip',
        description: `Guard ${guardName} failed`,
        event
      });
    }
    return result;
  }
}